import React from 'react';
import { Separator } from './ui/separator';
import { NewsletterForm } from './NewsletterForm';

export const Footer = ({ calendlyLink, instagramLink, logoUrl, onPrivacyClick }) => {
  const year = new Date().getFullYear();
  const handlePrivacy = (e) => {
    if (typeof onPrivacyClick === 'function') {
      e.preventDefault();
      onPrivacyClick();
    }
    // Otherwise let the link navigate to /privacy
  };
  return (
    <footer id="footer" className="bg-[#0B0C0D] text-white py-12 sm:py-14" data-testid="footer">
      <div className="max-w-[1120px] mx-auto px-5 sm:px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 mb-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-3">
              <img src={logoUrl} alt="Apex Athletic Mind" className="h-8 w-8 object-contain rounded-md" />
              <div className="leading-tight">
                <span className="font-heading font-bold text-[14px] text-white tracking-tight block">Apex Athletic Mind</span>
                <span className="font-mono-accent text-[8px] uppercase tracking-[0.18em] text-white/30">Consulting</span>
              </div>
            </div>
            <p className="text-[13px] text-white/35 leading-relaxed max-w-[38ch] mb-3">
              Mental performance consulting for serious and elite athletes — middle school through college and beyond. Building confidence, focus, and resilience.
            </p>

            {/* NAP block (Name · Address · Phone) — local SEO trust signal */}
            <address
              className="not-italic mt-1 mb-3 text-[12.5px] text-white/55 leading-[1.7]"
              data-testid="footer-nap"
              itemScope
              itemType="https://schema.org/ProfessionalService"
            >
              <span itemProp="name" className="sr-only">Apex Athletic Mind Consulting</span>
              <span
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
                className="block"
              >
                <span itemProp="streetAddress" className="block">
                  1820 Chapel Avenue W, Suite 300-100A PMB 70263
                </span>
                <span className="block">
                  <span itemProp="addressLocality">Cherry Hill</span>,{' '}
                  <span itemProp="addressRegion">NJ</span>{' '}
                  <span itemProp="postalCode">08002</span>{' '}
                  <span className="text-white/35">·</span>{' '}
                  Serving New Jersey, NY metro &amp; online nationwide
                </span>
              </span>
              <a
                href="tel:+18626930177"
                itemProp="telephone"
                className="inline-block mt-1 text-[#ACC0C6]/80 hover:text-white transition-colors"
                data-testid="footer-phone-link"
              >
                862-693-0177
              </a>
              <span className="text-white/35 text-[11px] ml-1">(Direct &middot; By appointment)</span>
              <span className="block text-[#ACC0C6]/80 mt-0.5">
                <a
                  href="tel:+18626930177"
                  className="hover:text-white transition-colors"
                  data-testid="footer-front-desk-link"
                >
                  862-693-0177
                </a>
                <span className="text-white/35 text-[11px] ml-1">(Front Desk &middot; Mon&ndash;Fri 9a&ndash;5p ET)</span>
              </span>
              <a href="mailto:volewis@apexathleticmind.com" itemProp="email" className="block text-[#ACC0C6]/80 hover:text-white transition-colors mt-0.5" data-testid="footer-email-link">volewis@apexathleticmind.com</a>
              <span className="block text-white/35 mt-0.5">Coach Lewis (Direct): By appointment &middot; 3:00 PM &ndash; 8:30 PM ET</span>
            </address>

            <div className="mt-6 max-w-md">
              <h4 className="font-mono-accent text-[9px] font-semibold text-white/50 uppercase tracking-[0.2em] mb-2.5">Newsletter</h4>
              <NewsletterForm source="footer" compact />
            </div>
          </div>

          <div>
            <h4 className="font-mono-accent text-[9px] font-semibold text-white/50 uppercase tracking-[0.2em] mb-3">Navigate</h4>
            <ul className="space-y-1.5">
              {[
                ['Athletes',          '/athletes'],
                ['Parents',           '/parents'],
                ['Coaches',           '/coaches'],
                ['Schools & Clubs',   '/schools'],
                ['The Method',        '/method'],
                ['About Coach Lewis', '/about'],
                ['Mindset Quiz',      '/quiz'],
                ['Apex Six IDP — $27','/assessment'],
                ['The Locker Room',   '/locker-room'],
                ['Pre-Game Playlists','/playlists'],
                ['Free Playbooks',    '/playbook'],
                ['Mental Performance Checklist', '/checklist'],
                ['The Apex Journal',  '/blog'],
                ['Contact',           '/contact'],
                ['Try the AI Coach',  '/app/login'],
              ].map(([l,h])=>(
                <li key={h}><a href={h} className="text-[13px] text-white/35 hover:text-white transition-colors" data-testid={`footer-link-${l.toLowerCase().replace(/\s+/g,'-').replace(/[—$]/g,'').replace(/-+/g,'-').replace(/^-|-$/g,'')}`}>{l}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono-accent text-[9px] font-semibold text-white/50 uppercase tracking-[0.2em] mb-3">Connect</h4>
            <ul className="space-y-1.5">
              <li><a href={calendlyLink} className="text-[13px] text-white/35 hover:text-white transition-colors" data-testid="footer-calendly-link">Book a Discovery Call</a></li>
              <li><a href={instagramLink} target="_blank" rel="noopener noreferrer" className="text-[13px] text-white/35 hover:text-white transition-colors" data-testid="footer-instagram-link">Instagram</a></li>
              <li>
                <a
                  href="/privacy"
                  onClick={handlePrivacy}
                  className="text-[13px] text-white/35 hover:text-white transition-colors text-left"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="/terms"
                  className="text-[13px] text-white/35 hover:text-white transition-colors"
                  data-testid="footer-terms-link"
                >
                  Terms and Conditions
                </a>
              </li>
              <li>
                <a
                  href="/disclaimer"
                  className="text-[13px] text-white/35 hover:text-white transition-colors"
                  data-testid="footer-disclaimer-link"
                >
                  Disclaimer
                </a>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="bg-white/6 mb-5" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-[11px] text-white/25" data-testid="footer-copyright">
            &copy; {year} Apex Athletic Mind Consulting LLC. All rights reserved. <span className="text-white/15 mx-1.5">|</span> Performance training &mdash; not therapy.
          </p>
          <p className="text-[11px] text-white/15">Train your mind. Elevate your game.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
